// Continuing a free cover into a book: the story idea that matches the reused cover, and when reuse is safe.
import { getTheme } from './themes';
import { UNIFIED_STORY_TEMPLATES, type UnifiedStoryTemplate } from '@/lib/unified-story-templates';

const THEME_TEMPLATE: Record<string, string | null> = {
  'superhero-family': 'superheroes',
  'space-adventure': 'space-mission',
  'royal-fairytale': null,
  'pirate-crew': 'pirate-adventure',
  'jungle-explorers': 'jungle-explorer',
  'winter-wonderland': 'holiday-adventure',
  'festival-of-lights': null,
};

const MAX_DETAILS = 2000; // CreateBookWizard formSchema: additionalComments max

/** The seed endpoint's signed photo URL lives 15 minutes; refetch a little before that. */
export const SEED_PHOTO_REFRESH_MS = 10 * 60_000;

function fit(text: string, max: number): string {
  if (text.length <= max) return text;
  const cut = text.lastIndexOf('\n', max);
  return text.slice(0, cut > 0 ? cut : max).trim();
}

export interface CoverStorySeed { template: UnifiedStoryTemplate | null; storyIdea: string }

/**
 * The story idea for a book continued from a free cover. The cover is reused unchanged, so the story's
 * outfits must match it: the cover scene comes first, and the template's own COSTUME line is dropped.
 */
export function coverStorySeed(themeId: string | null | undefined): CoverStorySeed | null {
  const theme = themeId ? getTheme(themeId) : undefined;
  if (!theme) return null;
  const templateId = THEME_TEMPLATE[theme.id] ?? null;
  const template = templateId ? UNIFIED_STORY_TEMPLATES.find((t) => t.id === templateId) ?? null : null;
  const cover = `COVER (already made; keep every outfit and the setting consistent with it): ${theme.sceneDescription}.`;
  const body = template
    ? template.storyContext.additionalComments
        .split('\n')
        .filter((line) => !line.trim().startsWith('COSTUME:'))
        .join('\n')
        .trim()
    : `STORY: A ${theme.displayName.toLowerCase()} adventure that begins in the cover scene and ends with a warm family moment.`;
  return { template, storyIdea: fit(`${cover}\n\n${body}`, MAX_DETAILS) };
}

/** Reuse the free cover only when it is ready and shows exactly the people selected for the book. */
export function canReuseCover(seed: { coverReady: boolean; faceCount: number | null } | null, selectedFaceCount: number): boolean {
  return !!seed?.coverReady && seed.faceCount != null && seed.faceCount === selectedFaceCount;
}

/** True when the seed's signed photo URL (fetched at `fetchedAt`) is close to expiring. */
export function seedPhotoIsStale(fetchedAt: number | null, now: number = Date.now()): boolean {
  return fetchedAt != null && now - fetchedAt > SEED_PHOTO_REFRESH_MS;
}

/** The continuation (title, story, style, reuse) is applied once per slug; later seed refreshes must not redo it. */
export function shouldApplyContinuation(appliedSlug: string | null, slug: string): boolean {
  return appliedSlug !== slug;
}

export type SeedOutcome = 'ok' | 'expired' | 'forbidden' | 'failed';

/** Classify a /api/free-cover/seed response. */
export function classifySeedResponse(status: number, body: { photoUrl?: unknown; code?: unknown } | null): SeedOutcome {
  if (status === 200) return typeof body?.photoUrl === 'string' && body.photoUrl ? 'ok' : 'failed';
  if (status === 410) return body?.code === 'photo_expired' ? 'expired' : 'failed';
  if (status === 403) return 'forbidden';
  return 'failed';
}

/** What a seed re-fetch (to renew the signed photo URL) is allowed to change. */
export function afterSeedRefresh(outcome: SeedOutcome) {
  const lost = outcome === 'expired' || outcome === 'forbidden';
  return { updatePhoto: outcome === 'ok', dropReuse: lost, clearPhoto: lost, toastExpired: outcome === 'expired' };
}
